/**
 * Sprint 3 — Sync recovery helpers.
 * Deterministic queue ops, auth-aware failure classification, active-session
 * backfill / deferred delete. Connectivity state machine stays in connectivityManager.
 *
 * Session lifecycle integrity: a finalized session (time_logs row exists) must
 * never be recreated as active_sessions. See docs/offline/session-lifecycle-integrity.md.
 */

import {
  readOrgScopedJson,
  writeOrgScopedJson,
} from "./orgCacheStorage";
import { clearLocalActiveSession } from "./activeSession";
import { devLog } from "./devLog";

export const PENDING_ACTIVE_SESSION_DELETES_KEY = "CP_PENDING_ACTIVE_SESSION_DELETES";

/** Fields that must not be republished on active_sessions insert/backfill. */
const ACTIVE_SESSION_INSERT_OMIT = new Set([
  "created_at",
  "updated_at",
]);

/**
 * Replace-or-append a pending timelog by session_id (local queue dedup).
 */
export function upsertPendingTimelogBySessionId(pending, nextLog) {
  if (!nextLog) {
    return Array.isArray(pending) ? [...pending] : [];
  }

  if (!nextLog.session_id) {
    return [...(pending || []), nextLog];
  }

  const without = (pending || []).filter(
    (log) => log?.session_id !== nextLog.session_id,
  );
  return [...without, nextLog];
}

/**
 * True when a Supabase/sync error indicates auth/JWT problems rather than network.
 * Auth failures must not flip connectivity to poor or destroy the queue.
 */
export function isAuthSyncError(error) {
  if (!error) {
    return false;
  }

  const status = error.status ?? error.statusCode ?? null;
  const code = String(error.code ?? "");
  const message = String(
    error.message || error.error_description || error.error || "",
  ).toLowerCase();

  if (status === 401 || status === 403) {
    return true;
  }

  if (code === "PGRST301" || code === "401" || code === "403") {
    return true;
  }

  return /jwt|unauthorized|not authenticated|invalid.?token|session.?expired|auth session missing|refresh.?token/i.test(
    message,
  );
}

/**
 * Ensure material commit retries reuse a stable idempotency key.
 */
export function ensureMaterialIdempotencyKey(
  log,
  createId = () => crypto.randomUUID(),
) {
  if (!log?.pending_materials?.length) {
    return log;
  }

  if (log.material_commit_idempotency_key) {
    return log;
  }

  return {
    ...log,
    material_commit_idempotency_key: createId(),
  };
}

/**
 * Strip local-only queue metadata before upserting time_logs.
 */
export function stripTimelogQueueFields(log) {
  if (!log) {
    return log;
  }

  const {
    pending_materials,
    materials_synced,
    material_commit_idempotency_key,
    pending_active_session_delete,
    ...timelogRow
  } = log;

  return timelogRow;
}

/**
 * Strip historical lifecycle metadata before inserting active_sessions.
 */
export function buildActiveSessionInsertPayload(session) {
  if (!session || typeof session !== "object") {
    return session;
  }

  const payload = {};
  for (const [key, value] of Object.entries(session)) {
    if (ACTIVE_SESSION_INSERT_OMIT.has(key)) {
      continue;
    }
    payload[key] = value;
  }
  return payload;
}

/**
 * Coalesce concurrent sync runs onto a single in-flight promise.
 */
export function createSyncMutex() {
  let inFlight = null;

  return async function runExclusive(fn) {
    if (inFlight) {
      return inFlight;
    }

    inFlight = (async () => {
      try {
        return await fn();
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  };
}

function readDeleteQueue(organizationId) {
  if (!organizationId) {
    return [];
  }

  try {
    const parsed = readOrgScopedJson(
      PENDING_ACTIVE_SESSION_DELETES_KEY,
      organizationId,
    );
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function writeDeleteQueue(organizationId, sessionIds) {
  if (!organizationId) {
    return;
  }

  writeOrgScopedJson(
    PENDING_ACTIVE_SESSION_DELETES_KEY,
    organizationId,
    Array.from(new Set((sessionIds || []).filter(Boolean))),
  );
}

/**
 * Queue an active_sessions delete for reconnect (idempotent by session_id).
 */
export function enqueuePendingActiveSessionDelete(sessionId, organizationId) {
  if (!sessionId || !organizationId) {
    return;
  }

  const existing = readDeleteQueue(organizationId);
  if (existing.includes(sessionId)) {
    return;
  }

  writeDeleteQueue(organizationId, [...existing, sessionId]);
}

export function getPendingActiveSessionDeletes(organizationId) {
  return readDeleteQueue(organizationId);
}

export function clearPendingActiveSessionDelete(sessionId, organizationId) {
  if (!sessionId || !organizationId) {
    return;
  }

  writeDeleteQueue(
    organizationId,
    readDeleteQueue(organizationId).filter((id) => id !== sessionId),
  );
}

async function defaultLoadFinalizedTimelog(supabase, sessionId) {
  const { data, error } = await supabase
    .from("time_logs")
    .select("id, session_id, end_time")
    .eq("session_id", sessionId)
    .maybeSingle();

  return { timelog: data || null, error: error || null };
}

/**
 * Invariant: if a time_logs row exists for session_id, the session is terminal.
 * Clear stale local ACTIVE mirror and delete any lingering server active row.
 */
export async function reconcileFinalizedActiveSession({
  supabase,
  actor,
  localSession,
  organizationId = null,
  clearLocalFn = clearLocalActiveSession,
  loadFinalizedTimelogFn = defaultLoadFinalizedTimelog,
}) {
  if (!localSession?.session_id || !actor?.supabase_user_id) {
    return { reconciled: false, reason: "no_local" };
  }

  const sessionId = localSession.session_id;
  const { timelog, error } = await loadFinalizedTimelogFn(supabase, sessionId);

  if (error) {
    return { reconciled: false, reason: "timelog_lookup_failed", error };
  }

  if (!timelog?.id) {
    return { reconciled: false, reason: "not_finalized" };
  }

  clearLocalFn(actor.supabase_user_id);

  const { error: deleteError } = await supabase
    .from("active_sessions")
    .delete()
    .eq("session_id", sessionId);

  if (organizationId) {
    clearPendingActiveSessionDelete(sessionId, organizationId);
  }

  console.info(
    "[session-lifecycle] reconciled finalized session; ACTIVE cleared",
    { sessionId, timeLogId: timelog.id },
  );
  devLog("SESSION LIFECYCLE RECONCILE", sessionId, timelog.id);

  if (deleteError && isAuthSyncError(deleteError)) {
    return {
      reconciled: true,
      reason: "finalized_local_cleared_delete_auth_blocked",
      timeLogId: timelog.id,
      error: deleteError,
    };
  }

  return {
    reconciled: true,
    reason: "finalized",
    timeLogId: timelog.id,
    deleteError: deleteError || null,
  };
}

/**
 * If a local active session exists and the server has no matching row, insert it.
 * Never recreates a session that already has a finalized time_logs row.
 */
export async function backfillLocalActiveSession({
  supabase,
  actor,
  localSession,
  orgScopedInsert,
  organizationId = null,
  clearLocalFn = clearLocalActiveSession,
  loadFinalizedTimelogFn = defaultLoadFinalizedTimelog,
}) {
  if (!localSession?.session_id || !actor?.supabase_user_id) {
    return { backfilled: false, reason: "no_local" };
  }

  const reconcile = await reconcileFinalizedActiveSession({
    supabase,
    actor,
    localSession,
    organizationId,
    clearLocalFn,
    loadFinalizedTimelogFn,
  });

  if (reconcile.reconciled) {
    return {
      backfilled: false,
      reason: "finalized_not_recreated",
      timeLogId: reconcile.timeLogId,
      error: reconcile.error || null,
    };
  }

  if (reconcile.reason === "timelog_lookup_failed") {
    return {
      backfilled: false,
      reason: "timelog_lookup_failed",
      error: reconcile.error,
    };
  }

  const { data, error } = await supabase
    .from("active_sessions")
    .select("session_id")
    .eq("session_id", localSession.session_id)
    .maybeSingle();

  if (error) {
    return { backfilled: false, reason: "lookup_failed", error };
  }

  if (data?.session_id) {
    return { backfilled: false, reason: "already_remote" };
  }

  const insertPayload = buildActiveSessionInsertPayload(localSession);

  const { error: insertError } = await orgScopedInsert(
    supabase,
    "active_sessions",
    [insertPayload],
    actor,
  );

  if (insertError) {
    return { backfilled: false, reason: "insert_failed", error: insertError };
  }

  return { backfilled: true, sessionId: localSession.session_id };
}

/**
 * Drain deferred active_sessions deletes. Failures leave ids queued.
 */
export async function syncPendingActiveSessionDeletes({
  supabase,
  organizationId,
}) {
  const pending = getPendingActiveSessionDeletes(organizationId);
  if (!pending.length) {
    return { deleted: 0, remaining: [], authBlocked: false, hadFailure: false };
  }

  const remaining = [];
  let deleted = 0;
  let authBlocked = false;
  let hadFailure = false;

  for (const sessionId of pending) {
    const { error } = await supabase
      .from("active_sessions")
      .delete()
      .eq("session_id", sessionId);

    if (error) {
      if (isAuthSyncError(error)) {
        authBlocked = true;
      } else {
        hadFailure = true;
      }
      remaining.push(sessionId);
      continue;
    }

    deleted += 1;
  }

  writeDeleteQueue(organizationId, remaining);

  return { deleted, remaining, authBlocked, hadFailure };
}
